import type { CSSProperties } from 'react';
import { MessageCircle } from 'lucide-react';

import type {
  Annotation,
  AnnotationRect,
  AnnotationReference,
  AnnotationResolution,
  AnnotationTextColor,
} from './annotationTypes';
import {
  getAnnotationHighlightId,
} from './annotationTypes';
import { AnnotationPopover } from './AnnotationPopover';
import styles from './HighlightOverlay.module.css';

export interface MeasuredAnnotation {
  annotation: Annotation;
  rects: AnnotationRect[];
  resolution: Extract<AnnotationResolution, { status: 'resolved' }>;
}

interface HighlightOverlayProps {
  activeAnnotation: AnnotationReference | null;
  annotations: MeasuredAnnotation[];
  cardRight: number;
  highlightColor: string;
  layerWidth: number;
  noteTextColor: AnnotationTextColor;
  onCloseNoteAnnotation: (annotation: Annotation) => void;
  onDeleteAnnotation: (annotation: Annotation) => Promise<void> | void;
  onOpenAnnotation: (reference: AnnotationReference | null) => void;
  onToggleNoteAnnotation: (annotation: Annotation) => void;
  onUpdateAnnotation: (annotation: Annotation) => Promise<void> | void;
  openNoteIds: Set<string>;
  proseRight: number;
  showNoteMarkers: boolean;
}

const NOTE_MARKER_SIZE = 28;
const NOTE_MARKER_RIGHT_OFFSET = 12;

function getMarkerLeft(
  layerWidth: number,
  proseRight: number,
  cardRight: number,
  rect: AnnotationRect,
) {
  if (layerWidth <= 0) {
    return rect.left + rect.width + 8;
  }

  if (cardRight > 0) {
    return cardRight - NOTE_MARKER_SIZE / 2;
  }

  const preferredLeft =
    proseRight > 0
      ? proseRight + NOTE_MARKER_RIGHT_OFFSET
      : rect.left + rect.width + 8;
  const edgeLeft = layerWidth - NOTE_MARKER_RIGHT_OFFSET - NOTE_MARKER_SIZE;

  return Math.max(
    rect.left + rect.width + 8,
    Math.min(preferredLeft, edgeLeft),
  );
}

export function HighlightOverlay({
  activeAnnotation,
  annotations,
  cardRight,
  highlightColor,
  layerWidth,
  noteTextColor,
  onCloseNoteAnnotation,
  onDeleteAnnotation,
  onOpenAnnotation,
  onToggleNoteAnnotation,
  onUpdateAnnotation,
  openNoteIds,
  proseRight,
  showNoteMarkers,
}: HighlightOverlayProps) {
  if (annotations.length === 0) {
    return null;
  }

  const activeMeasuredAnnotation = annotations.find(({ annotation }) => {
    if (
      activeAnnotation?.annotationId !== annotation.id ||
      activeAnnotation.contentUnitId !== annotation.contentUnitId
    ) {
      return false;
    }

    return annotation.kind === 'highlight';
  });
  const activeRect = activeMeasuredAnnotation?.rects[0] ?? null;

  const noteMarkers = annotations.flatMap(({ annotation, rects }) => {
    if (annotation.kind !== 'note') {
      return [];
    }

    const firstRect = rects[0];

    if (!firstRect) {
      return [];
    }

    return [
      {
        annotation,
        rect: firstRect,
      },
    ];
  });

  return (
    <>
      <div className={styles.overlay}>
        {annotations.map(({ annotation, rects }) => {
          const reference = {
            annotationId: annotation.id,
            contentUnitId: annotation.contentUnitId,
          } satisfies AnnotationReference;
          const isActive =
            activeAnnotation?.annotationId === annotation.id &&
            activeAnnotation.contentUnitId === annotation.contentUnitId;

          return (
            <div className={styles.annotationLayer} key={annotation.id}>
              {rects.map((rect, index) => (
                <div
                  className={styles.highlightRect}
                  data-active={isActive ? 'true' : 'false'}
                  data-kind={annotation.kind}
                  id={index === 0 ? getAnnotationHighlightId(reference) : undefined}
                  key={`${annotation.id}-${index}`}
                  style={
                    {
                      '--annotation-highlight-color': highlightColor,
                      height: rect.height,
                      left: rect.left,
                      top: rect.top,
                      width: rect.width,
                    } as CSSProperties
                  }
                />
              ))}
            </div>
          );
        })}
      </div>

      {showNoteMarkers && noteMarkers.length > 0 ? (
        <div className={styles.markerLayer}>
          {noteMarkers.map(({ annotation, rect }) => {
            const isActive =
              activeAnnotation?.annotationId === annotation.id &&
              activeAnnotation.contentUnitId === annotation.contentUnitId;
            const isOpen = openNoteIds.has(annotation.id);

            return (
              <button
                aria-expanded={isOpen}
                aria-label="View note"
                className={styles.noteMarker}
                data-active={isActive || isOpen ? 'true' : 'false'}
                key={annotation.id}
                onClick={(event) => {
                  event.stopPropagation();
                  onToggleNoteAnnotation(annotation);
                }}
                style={
                  {
                    '--annotation-highlight-color': highlightColor,
                    left: getMarkerLeft(layerWidth, proseRight, cardRight, rect),
                    top: rect.top,
                  } as CSSProperties
                }
                type="button"
              >
                <MessageCircle aria-hidden="true" size={12} />
              </button>
            );
          })}
        </div>
      ) : null}

      {showNoteMarkers
        ? noteMarkers
            .filter(({ annotation }) => openNoteIds.has(annotation.id))
            .map(({ annotation, rect }) => (
              <AnnotationPopover
                anchorRect={{
                  height: NOTE_MARKER_SIZE,
                  left: getMarkerLeft(layerWidth, proseRight, cardRight, rect),
                  top: rect.top,
                  width: NOTE_MARKER_SIZE,
                }}
                annotation={annotation}
                key={`popover-${annotation.id}`}
                noteTextColor={annotation.textColor ?? noteTextColor}
                onDelete={onDeleteAnnotation}
                onOpenChange={(nextOpen) => {
                  if (!nextOpen) {
                    onCloseNoteAnnotation(annotation);
                  }
                }}
                onSave={onUpdateAnnotation}
                open
              />
            ))
        : null}

      {activeMeasuredAnnotation && activeRect ? (
        <AnnotationPopover
          anchorRect={activeRect}
          annotation={activeMeasuredAnnotation.annotation}
          onDelete={onDeleteAnnotation}
          onOpenChange={(nextOpen) =>
            onOpenAnnotation(
              nextOpen
                ? {
                    annotationId: activeMeasuredAnnotation.annotation.id,
                    contentUnitId: activeMeasuredAnnotation.annotation.contentUnitId,
                  }
                : null,
            )
          }
          onSave={onUpdateAnnotation}
          noteTextColor={activeMeasuredAnnotation.annotation.textColor ?? noteTextColor}
          open
        />
      ) : null}
    </>
  );
}
